import React, { useEffect, useRef } from 'react';
import { loadGooglePlaces, normalizePlace } from 'utility/googleMaps';

// Wrapper around google.maps.places.PlaceAutocompleteElement (the replacement for
// the deprecated google.maps.places.Autocomplete). Calls onPlaceSelected with a
// place normalized to the legacy shape so existing consumers stay unchanged.
function PlaceAutocomplete({
    apiKey,
    onPlaceSelected,
    includedRegionCodes,
    locationRestriction,
    locationBias,
    placeholder,
    className,
}) {
    const containerRef = useRef(null);
    const onPlaceSelectedRef = useRef(onPlaceSelected);
    onPlaceSelectedRef.current = onPlaceSelected;

    useEffect(() => {
        let cancelled = false;
        let element = null;

        loadGooglePlaces(apiKey)
            .then((places) => {
                if (cancelled || !containerRef.current) {
                    return;
                }

                // locationBias (soft preference) and locationRestriction (hard
                // boundary) are mutually exclusive in the Places API — passing both
                // throws, so prefer the restriction when callers supply both.
                const options = {};
                if (includedRegionCodes) options.includedRegionCodes = includedRegionCodes;
                if (locationRestriction) options.locationRestriction = locationRestriction;
                else if (locationBias) options.locationBias = locationBias;

                element = new places.PlaceAutocompleteElement(options);

                if (placeholder) {
                    element.setAttribute('placeholder', placeholder);
                }

                element.addEventListener('gmp-select', async ({ placePrediction }) => {
                    if (!placePrediction) {
                        return;
                    }

                    const place = placePrediction.toPlace();
                    await place.fetchFields({ fields: ['addressComponents', 'location', 'formattedAddress'] });

                    onPlaceSelectedRef.current?.(normalizePlace(place));
                });

                containerRef.current.appendChild(element);
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to load Google Maps Places Autocomplete', error);
            });

        return () => {
            cancelled = true;
            if (element) {
                element.remove();
            }
        };
    }, [apiKey, includedRegionCodes, locationRestriction, locationBias, placeholder]);

    return <div ref={containerRef} className={className} />;
}

export default PlaceAutocomplete;